import { useEffect, useState } from "react";
import { api } from "../api";
import { fmtCompact, fmtMoney, fmtMonth, fmtSigned, fmtUnits, timeAgo } from "../format";
import type { Answer, AnswerView as View, SeriesMeta, Suggestion } from "../types";
import { ColumnChart, DivergingBars, LineChart, type LineBand, type LineSeries } from "./charts";
import RunSummary from "./RunSummary";

/** Series colours in order: the first is the subject, the second the comparison. */
const COLORS = ["var(--series-target)", "var(--series-base)", "var(--series-c3)", "var(--series-c4)",
                "var(--series-neg)", "var(--good)"];

type Cell = string | number | null;

const num = (c: Cell): number | null => (typeof c === "number" ? c : c == null || c === "" ? null : Number(c));
const isMonth = (s: string) => /^\d{4}-\d{2}$/.test(s);

function fmtBy(unit?: string) {
  return (v: number | null | undefined) =>
    v == null ? "—" : unit === "$" ? fmtMoney(v) : unit === "%" ? `${v.toFixed(1)}%` : fmtUnits(v);
}
const fmtLabel = (c: Cell) => (c == null ? "—" : typeof c === "string" && isMonth(c) ? fmtMonth(c) : String(c));

/** Column metadata: from the server when it sent it (demand views), else every column after the label. */
function metas(v: View): (SeriesMeta & { col: number })[] {
  const cols = v.columns ?? [];
  if (v.series?.length && v.series.length === cols.length - 1) return v.series.map((s, i) => ({ ...s, col: i + 1 }));
  const idx = v.numeric_columns ?? cols.map((_, i) => i).filter((i) => i > 0);
  return idx.map((i) => ({ key: cols[i], label: cols[i], role: "data", unit: v.unit ?? "", style: "solid", col: i }));
}

/** One table or chart of an answer. The numbers come from Kinaxis, computed when the card opens. */
export default function AnswerView({ view, compact = false }: { view: View; compact?: boolean }) {
  const [asTable, setAsTable] = useState(false);
  if (view.error) {
    return (
      <figure className="answer-view">
        <figcaption>{view.title}</figcaption>
        <div className="small error-text">Couldn’t compute this view: {view.error}</div>
      </figure>
    );
  }
  const rows = view.rows ?? [];
  const caption = (
    <figcaption>
      <span>{view.title}</span>
      <span className="muted small">
        {view.window?.from && view.window.from !== "0000-01" ? `${fmtMonth(view.window.from)} – ${fmtMonth(view.window.to)}` : view.table ?? ""}
        {view.unit && view.unit !== "mixed" ? ` · ${view.unit}` : ""}
        {view.kind !== "table" && view.kind !== "kpi" && rows.length > 0 && (
          <button type="button" className="link-btn" onClick={(e) => { e.stopPropagation(); setAsTable((t) => !t); }}>
            {asTable ? "Chart" : "Table"}
          </button>
        )}
      </span>
    </figcaption>
  );
  const foot = (
    <>
      {(view.notes ?? []).map((n) => <div key={n} className="small muted">{n}</div>)}
      {view.truncated && <div className="small muted">First {rows.length} rows only.</div>}
    </>
  );
  if (!rows.length) return <figure className="answer-view">{caption}<div className="small muted">No data for this view.</div>{foot}</figure>;

  const metrics = view.metrics && Object.keys(view.metrics).length > 0 && (
    <div className="answer-kpis">
      {Object.entries(view.metrics).map(([k, v]) => (
        <div key={k} className="answer-kpi"><span className="muted small">{k}</span>
          <b>{v == null ? "—" : k.endsWith("%") ? `${v.toFixed(1)}%` : fmtUnits(v)}</b></div>
      ))}
    </div>
  );

  let body: JSX.Element;
  if (asTable || view.kind === "table") body = <DataTable view={view} limit={compact ? 6 : rows.length} />;
  else if (view.kind === "kpi") body = <Kpis view={view} />;
  else if (view.kind === "line") body = <Lines view={view} compact={compact} />;
  else if (view.kind === "stacked") body = <Stacked view={view} compact={compact} />;
  else body = <Bars view={view} compact={compact} />;

  return <figure className="answer-view">{caption}{metrics}{body}{foot}</figure>;
}

function Kpis({ view }: { view: View }) {
  const ms = metas(view).filter((m) => m.role !== "derived");
  const totals = view.totals && Object.keys(view.totals).length ? view.totals
    : Object.fromEntries(ms.map((m) => [m.label, (view.rows ?? []).reduce((s, r) => s + (num(r[m.col]) ?? 0), 0)]));
  const unitOf = Object.fromEntries(ms.map((m) => [m.label, m.unit]));
  return (
    <div className="answer-kpis">
      {Object.entries(totals).map(([k, v]) => (
        <div key={k} className="answer-kpi"><span className="muted small">{k}</span>
          <b>{unitOf[k] === "$" ? fmtMoney(v) : fmtCompact(v)}</b></div>
      ))}
    </div>
  );
}

/**
 * Lines over time. History solid, forecasts dashed, a rule at the run date; an optimistic /
 * pessimistic pair as a band around its base; a difference as columns underneath; series in
 * different units (units vs $) as separate small charts, never on a shared axis.
 */
function Lines({ view, compact }: { view: View; compact: boolean }) {
  const rows = view.rows ?? [];
  const labels = rows.map((r) => String(r[0]));
  const ms = metas(view);
  const marker = view.anchor ? labels.indexOf(view.anchor) : -1;
  const plotted = ms.filter((m) => m.style !== "pct" && m.style !== "diff" && m.style !== "band_low" && m.style !== "band_high");
  const colorOf = new Map(plotted.map((m, i) => [m.key, COLORS[i % COLORS.length]]));
  const units = [...new Set(plotted.map((m) => m.unit))];
  const highs = ms.filter((m) => m.style === "band_high");
  const diff = ms.find((m) => m.style === "diff");

  const charts = units.map((unit) => {
    const series: LineSeries[] = plotted.filter((m) => m.unit === unit).map((m) => ({
      name: m.label, color: colorOf.get(m.key)!, dashed: m.style === "dashed" || m.style === "ly",
      values: rows.map((r) => num(r[m.col])),
    }));
    const bands: LineBand[] = highs.filter((h) => h.unit === unit).flatMap((h) => {
      const low = ms.find((m) => m.style === "band_low" && m.band_of === h.band_of);
      if (!low) return [];
      return [{ name: `${low.label} – ${h.label}`, color: colorOf.get(h.band_of ?? "") ?? COLORS[0],
                low: rows.map((r) => num(r[low.col])), high: rows.map((r) => num(r[h.col])) }];
    });
    return (
      <div key={unit}>
        {units.length > 1 && <div className="small muted">{unit === "$" ? "Value ($)" : unit === "%" ? "Share (%)" : "Units"}</div>}
        <LineChart height={compact ? 140 : units.length > 1 ? 160 : 210} labels={labels} series={series} bands={bands}
                   marker={marker >= 0 && view.series?.some((s) => s.role === "history") ? marker : undefined}
                   format={fmtBy(unit)} />
      </div>
    );
  });
  return (
    <>
      {charts}
      {diff && !compact && (
        <div>
          <div className="small muted">{diff.label}</div>
          <ColumnChart height={110} name={diff.label}
                       rows={rows.map((r) => ({ label: String(r[0]), value: num(r[diff.col]) ?? 0 }))} />
        </div>
      )}
    </>
  );
}

/** Groups side by side: one bar per series, or diverging bars when the view compares two. */
function Bars({ view, compact }: { view: View; compact: boolean }) {
  const rows = view.rows ?? [];
  const ms = metas(view);
  const diff = ms.find((m) => m.style === "diff");
  const pct = ms.find((m) => m.style === "pct");
  const limit = compact ? 6 : rows.length;
  if (diff) {
    const a = ms[0], b = ms[1];
    return (
      <DivergingBars format={(v) => (diff.unit === "$" ? `${v > 0 ? "+" : ""}${fmtMoney(v)}` : fmtSigned(v))}
                     rows={rows.slice(0, limit).map((r) => ({
                       label: fmtLabel(r[0]), value: num(r[diff.col]) ?? 0,
                       note: `${a.label} ${fmtBy(a.unit)(num(r[a.col]))} vs ${b.label} ${fmtBy(b.unit)(num(r[b.col]))}` +
                             (pct && num(r[pct.col]) != null ? ` (${num(r[pct.col])! > 0 ? "+" : ""}${num(r[pct.col])!.toFixed(1)}%)` : ""),
                     }))} />
    );
  }
  const shown = ms.filter((m) => m.role !== "derived").slice(0, 4);
  const max = Math.max(1, ...rows.flatMap((r) => shown.map((m) => Math.abs(num(r[m.col]) ?? 0))));
  return (
    <>
      <div className="hbars" role="img" aria-label={view.title}>
        {rows.slice(0, limit).map((r, k) => (
          <div key={k} className="hbar-row" title={shown.map((m) => `${m.label}: ${fmtBy(m.unit)(num(r[m.col]))}`).join(" · ")}>
            <span className={`hbar-label${view.dimension === "Part" ? " mono" : ""}`}>{fmtLabel(r[0])}</span>
            <span className="hbar-bars">
              {shown.map((m, n) => (
                <span key={m.key} className="hbar" style={{ width: `${(Math.abs(num(r[m.col]) ?? 0) / max) * 100}%`, background: COLORS[n] }} />
              ))}
            </span>
            <span className="hbar-val">{fmtBy(shown[0].unit === "$" ? "$" : "")(num(r[shown[0].col]))}</span>
          </div>
        ))}
        {rows.length > limit && <div className="small muted">+{rows.length - limit} more in details</div>}
      </div>
      <Legend names={shown.map((m) => m.label)} />
    </>
  );
}

/** Parts of a whole per period (or per group): stacked columns, one colour per segment. */
function Stacked({ view, compact }: { view: View; compact: boolean }) {
  const rows = view.rows ?? [];
  const ms = metas(view).filter((m) => m.role !== "derived");
  const totals = rows.map((r) => ms.reduce((s, m) => s + Math.max(0, num(r[m.col]) ?? 0), 0));
  const max = Math.max(1, ...totals);
  const share = ms.every((m) => m.unit === "%");
  const fmt = fmtBy(ms[0]?.unit);
  return (
    <>
      <div className="stack-cols" style={{ height: compact ? 120 : 180 }} role="img" aria-label={view.title}>
        {rows.map((r, k) => (
          <div key={k} className="stack-col" title={`${fmtLabel(r[0])}\n` + ms.map((m) => `${m.label}: ${fmt(num(r[m.col]))}`).join("\n")}>
            <div className="stack-bar" style={{ height: `${(share ? 100 : (totals[k] / max) * 100)}%` }}>
              {ms.map((m, n) => {
                const v = Math.max(0, num(r[m.col]) ?? 0);
                return <span key={m.key} style={{ flexGrow: v, background: COLORS[n % COLORS.length] }} />;
              })}
            </div>
            <span className="stack-label">{fmtLabel(r[0])}</span>
          </div>
        ))}
      </div>
      <Legend names={ms.map((m) => m.label)} />
    </>
  );
}

function DataTable({ view, limit }: { view: View; limit: number }) {
  const cols = view.columns ?? [];
  const rows = view.rows ?? [];
  const unitOf = new Map(metas(view).map((m) => [m.col, m.unit]));
  return (
    <>
      <div className="answer-table">
        <table className="data compact">
          <thead><tr>{cols.map((c, i) => <th key={i} className={i ? "num" : ""}>{c}</th>)}</tr></thead>
          <tbody>
            {rows.slice(0, limit).map((r, k) => (
              <tr key={k}>{r.map((c, i) => (
                <td key={i} className={i ? "num" : view.dimension === "Part" ? "mono" : ""}>
                  {i === 0 ? fmtLabel(c) : typeof c === "number" ? (unitOf.get(i) === "%" ? `${c > 0 && cols[i] === "Diff %" ? "+" : ""}${c.toFixed(1)}%` : fmtBy(unitOf.get(i))(c)) : c ?? "—"}
                </td>
              ))}</tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > limit && <div className="small muted">+{rows.length - limit} more rows in details</div>}
    </>
  );
}

function Legend({ names }: { names: string[] }) {
  if (names.length < 2) return null;
  return (
    <div className="answer-legend small">
      {names.map((n, i) => <span key={n}><i style={{ background: COLORS[i % COLORS.length] }} />{n}</span>)}
    </div>
  );
}

/** The full answer to a question: the agent's text, every view (computed from Kinaxis now) and follow-ups. */
export function AnswerPanel({ boardId, intentId, runId = "", refreshKey, onFork, showSummary = true }: {
  boardId: string; intentId: string; runId?: string; refreshKey?: string; onFork?: (text: string) => void;
  showSummary?: boolean;
}) {
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    setAnswer(null);
    setError("");
    api.answer(boardId, intentId, runId).then((a) => live && setAnswer(a)).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [boardId, intentId, runId, refreshKey]);

  if (error) return <div className="small error-text">{error}</div>;
  if (!answer) return <div className="row small muted"><div className="spinner" /> Reading from Kinaxis…</div>;
  return (
    <div className="answer">
      {showSummary && answer.summary && <RunSummary text={answer.summary} onAct={onFork} />}
      {answer.views.map((v, i) => <AnswerView key={i} view={v} />)}
      {answer.views.length > 0 && (
        <div className="small muted">Figures read from Kinaxis just now{answer.asked_at ? `; asked ${timeAgo(answer.asked_at)}` : ""}.</div>
      )}
      {onFork && answer.suggestions.length > 0 && <Suggestions items={answer.suggestions} onPick={onFork} />}
    </div>
  );
}

/** Follow-up intents an answer offers. Picking one opens the composer, linked to the question. */
export function Suggestions({ items, onPick, max = 3 }: { items: Suggestion[]; onPick: (text: string) => void; max?: number }) {
  return (
    <div className="suggestions">
      {items.slice(0, max).map((s) => (
        <button key={s.text} type="button" className="suggestion" title={s.why || s.text}
                onClick={(e) => { e.stopPropagation(); onPick(s.text); }}>
          <span aria-hidden>⑂</span><span className="suggestion-text">{s.text}</span>
        </button>
      ))}
    </div>
  );
}
